// ============================================================================
// GESTION MULTI-SITE (M9-3, lot 3C)
// ============================================================================
// « Dans l'onglet gestion multi-site on a uniquement : plannings les uns sur les
//   autres, rendez-vous métier les uns sur les autres, organisation du jour,
//   balance des heures. Quand je dis les uns sur les autres, c'est que je veux
//   que l'onglet affiche toutes les pharmacies et qu'on ait qu'à descendre. »
//
// C'est donc une VUE D'ENSEMBLE, en lecture : on parcourt, on repère, et on
// bascule sur l'officine concernée pour agir. Rien n'y est modifiable — sinon il
// faudrait dupliquer ici toute la mécanique d'édition de quatre écrans.
// ============================================================================
import { useMemo, useState } from 'react';
import { PageHelp } from '@/components/ui/PageHelp';
import { startOfWeek, addDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Calendar, Syringe, UserCheck, Scale, ChevronLeft, ChevronRight,
  Building2, AlertTriangle, ArrowRight,
} from 'lucide-react';
import {
  useSites, useSwitchSite,
  useSiteSchedules, useSiteEmployees, useSiteAppointments,
  useSiteOrganization, useSiteHoursBalance, useSitePharmacy,
  type Site,
} from '@/api/resources';
import { positionStyle, byPositionThenName } from '@/lib/positions';
import { isEmployedBetween, isEmployedOn } from '@/lib/employment';
import {
  computeDailyHours, slotLabel, isWorkingDay, closedByWeekday, weekDayCount, type Slot,
} from '@/lib/schedule';
import { absenceMeta, ABSENCE_CELL_MUTED } from '@/lib/absenceTypes';
import type { Schedule, ScheduleStatus } from '@/types';

type ViewId = 'planning' | 'appointments' | 'organization' | 'balance';

const VIEWS: { id: ViewId; label: string; icon: typeof Calendar }[] = [
  { id: 'planning', label: 'Plannings', icon: Calendar },
  { id: 'appointments', label: 'Rendez-vous métier', icon: Syringe },
  { id: 'organization', label: 'Organisation du jour', icon: UserCheck },
  { id: 'balance', label: 'Balance des heures', icon: Scale },
];

// Le dimanche n'apparaît que pour les officines qui ouvrent ce jour-là.
const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

// Repli quand une journée d'absence n'a pas de type précis — même table que
// l'écran Planning.
const STATUS_LABEL: Partial<Record<ScheduleStatus, { label: string; dot: string }>> = {
  LEAVE:     { label: 'Congé',            dot: '#64748b' },
  SICK:      { label: 'Arrêt maladie',    dot: '#dc2626' },
  ACCIDENT:  { label: 'Accident travail', dot: '#dc2626' },
  MATERNITY: { label: 'Maternité',        dot: '#db2777' },
};

/** Le motif d'absence à afficher dans la case, ou `null` si le salarié est là. */
function absenceOf(s: Schedule | undefined): { label: string; dot: string } | null {
  if (!s) return null;
  if (s.absenceType) {
    const m = absenceMeta(s.absenceType);
    return { label: m.short, dot: m.dot };
  }
  return STATUS_LABEL[s.status] ?? null;
}

/** Bandeau d'une officine : son nom, et le raccourci pour aller y travailler. */
function SiteHeader({ site, subtitle }: { site: Site; subtitle?: string }) {
  const switchSite = useSwitchSite();

  // La destination est passée à la bascule : c'est elle qui recharge la page,
  // donc un `navigate()` après coup n'aurait jamais le temps de s'exécuter.
  const goThere = () => {
    switchSite.mutate({ siteId: site.id, to: '/planning' });
  };

  return (
    <div className="flex items-center justify-between gap-4 mb-2.5">
      <div className="flex items-center gap-2 min-w-0">
        <Building2 size={15} className="text-slate-400 flex-shrink-0" />
        <h2 className="text-[14px] font-semibold text-slate-900 truncate">{site.name}</h2>
        {site.city && <span className="text-[11px] text-slate-400">{site.city}</span>}
        {subtitle && <span className="text-[11px] text-slate-500">· {subtitle}</span>}
      </div>
      <button
        onClick={goThere}
        disabled={switchSite.isPending}
        className="flex items-center gap-1 text-[11.5px] text-primary hover:underline flex-shrink-0 disabled:opacity-50"
      >
        Ouvrir cette officine <ArrowRight size={12} />
      </button>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-[11.5px] text-slate-400 italic py-3">{children}</div>;
}

// ---------------------------------------------------------------------------
// 1. Plannings empilés
// ---------------------------------------------------------------------------
function SitePlanning({ site, weekStart }: { site: Site; weekStart: Date }) {
  const { data: pharmacy } = useSitePharmacy(site.id);
  // Chaque officine du groupe a ses propres horaires : l'une peut ouvrir le
  // dimanche et pas l'autre. La largeur de la grille se décide donc ici, site
  // par site, et non pour toute la page.
  const dayCount = useMemo(() => weekDayCount(pharmacy?.openingHours), [pharmacy]);

  const from = format(weekStart, 'yyyy-MM-dd');
  const to = format(addDays(weekStart, dayCount - 1), 'yyyy-MM-dd');
  const { data: schedules } = useSiteSchedules(site.id, from, to);
  const { data: employees } = useSiteEmployees(site.id);

  // Même règle que l'écran Planning : pas de ligne pour un contrat qui ne croise
  // pas la semaine affichée.
  const weekEnd = addDays(weekStart, dayCount - 1);
  const rows = useMemo(
    () =>
      employees
        ? employees.filter((e) => isEmployedBetween(e, weekStart, weekEnd)).sort(byPositionThenName)
        : [],
    [employees, weekStart, weekEnd],
  );
  const byKey = useMemo(() => {
    const m = new Map<string, Schedule>();
    (schedules ?? []).forEach((s) => m.set(`${s.employeeId}|${s.date.slice(0, 10)}`, s));
    return m;
  }, [schedules]);
  const closed = useMemo(() => closedByWeekday(pharmacy?.openingHours), [pharmacy]);
  // Fériés fermés de CETTE officine (« Ouvert ? Non ») : jour de fermeture,
  // colonne grisée — chaque site a sa propre liste.
  const closedHolidays = useMemo(() => {
    const m = new Map<string, string>();
    (pharmacy?.holidayRules ?? []).forEach((h: any) => {
      if (!h.isOpen) m.set(String(h.date).slice(0, 10), h.name);
    });
    return m;
  }, [pharmacy]);

  if (rows.length === 0) return <Empty>Aucun salarié dans cette officine.</Empty>;

  const COLS = `150px repeat(${dayCount}, minmax(122px, 1fr)) 58px`;

  return (
    <div className="overflow-x-auto">
      <div style={{ minWidth: 208 + dayCount * 134 }}>
        <div className="grid text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200 pb-1.5" style={{ gridTemplateColumns: COLS }}>
          <div />
          {DAYS.slice(0, dayCount).map((d, i) => {
            const holiday = closedHolidays.get(format(addDays(weekStart, i), 'yyyy-MM-dd'));
            return (
              <div key={d} className={`text-center ${holiday ? 'bg-slate-200/70 rounded' : ''}`} title={holiday ? `Férié, officine fermée : ${holiday}` : undefined}>
                {d.slice(0, 3)} <span className="text-slate-400 normal-case tracking-normal">{format(addDays(weekStart, i), 'd/MM')}</span>
                {holiday && <div className="normal-case tracking-normal text-[9px] font-semibold text-slate-500 truncate">Férié fermé</div>}
              </div>
            );
          })}
          <div className="text-right">Total</div>
        </div>

        {rows.map((emp) => {
          const st = positionStyle(emp.position);
          let total = 0;
          const cells = Array.from({ length: dayCount }).map((_, i) => {
            const date = addDays(weekStart, i);
            const s = byKey.get(`${emp.id}|${format(date, 'yyyy-MM-dd')}`);
            total += computeDailyHours(s);

            const holiday = closedHolidays.get(format(date, 'yyyy-MM-dd'));
            const weekdayShut = closed.get(date.getDay());
            const shut = holiday ? { morning: true, afternoon: true } : weekdayShut;
            const abs = absenceOf(s);

            // Hors contrat : pas encore arrivé, ou déjà parti.
            if (!isEmployedOn(emp, date)) {
              return (
                <div key={i} className="px-0.5">
                  <div className="rounded border border-slate-100 bg-slate-50/60 text-[9.5px] text-center py-1.5 text-slate-300">—</div>
                </div>
              );
            }

            // 1. Absence : case effacée, le motif ne subsiste que dans la
            //    pastille — exactement comme dans l'écran Planning.
            if (abs) {
              return (
                <div key={i} className="px-0.5">
                  <div title={abs.label} className={`rounded border flex items-center justify-center gap-1 text-[9.5px] py-1.5 ${ABSENCE_CELL_MUTED}`}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: abs.dot }} />
                    {abs.label}
                  </div>
                </div>
              );
            }

            // 2. Officine fermée toute la journée — sauf si le salarié y
            //    travaille quand même (ménage, magasinier : heures comptées).
            if (shut?.morning && shut?.afternoon && !isWorkingDay(s)) {
              return (
                <div key={i} className="px-0.5">
                  <div title={holiday ? `Férié, officine fermée : ${holiday}` : undefined} className="rounded border border-slate-200 bg-slate-100 text-[9.5px] text-center py-1.5 font-medium text-slate-400">
                    {holiday ? 'Férié' : 'Fermé'}
                  </div>
                </div>
              );
            }

            // 3. Journée normale : les deux demi-journées, avec leurs horaires.
            return (
              <div key={i} className="px-0.5 grid grid-cols-2 gap-0.5">
                {(['morning', 'afternoon'] as Slot[]).map((slot) => {
                  const isWork =
                    s?.status === 'WORK' && !!(slot === 'morning' ? s.morningStart : s.afternoonStart);
                  // Une journée peut se partager entre deux officines : l'API
                  // renvoie ici la moitié qui appartient à CE site, et dit où se
                  // passe l'autre. Une demi-journée faite ailleurs n'est pas un
                  // repos — on la nomme plutôt que de laisser un tiret.
                  const halfSiteId = slot === 'morning' ? s?.morningSiteId : s?.afternoonSiteId;
                  const elsewhere = !isWork && !!halfSiteId && halfSiteId !== site.id;
                  const slotClosed = slot === 'morning' ? shut?.morning : shut?.afternoon;
                  if (slotClosed && !isWork) {
                    return (
                      <div key={slot} title={`Fermé ${slot === 'morning' ? 'le matin' : "l'après-midi"}`} className="rounded border border-slate-200 bg-slate-100 text-[9px] text-center py-1.5 font-medium text-slate-400">
                        Fermé
                      </div>
                    );
                  }
                  return (
                    <div
                      key={slot}
                      // Férié fermé : un créneau posé reste gris (heures comptées).
                      style={
                        isWork
                          ? holiday
                            ? { backgroundColor: '#e2e8f0', color: '#64748b', borderColor: '#cbd5e1' }
                            : { backgroundColor: st.bg, color: st.text, borderColor: st.bg }
                          : undefined
                      }
                      title={
                        isWork && holiday
                          ? `Planifié un jour férié où l'officine est fermée (${holiday}) — heures comptées`
                          : elsewhere
                          ? 'Cette demi-journée se fait dans une autre officine'
                          : undefined
                      }
                      className={`rounded border text-[9px] px-1 py-1.5 text-center tabular-nums truncate ${
                        isWork
                          ? 'font-semibold'
                          : elsewhere
                          ? 'bg-slate-100 text-slate-400 border-slate-200 font-medium'
                          : 'bg-slate-50/60 text-slate-300 border-slate-200 font-medium'
                      }`}
                    >
                      {elsewhere ? '→ ailleurs' : slotLabel(s, slot)}
                    </div>
                  );
                })}
              </div>
            );
          });

          return (
            <div key={emp.id} className="grid items-center border-b border-slate-100 py-1" style={{ gridTemplateColumns: COLS }}>
              <div className="flex items-center gap-1.5 min-w-0 pr-2">
                <span className="w-1 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: st.bg }} />
                <span className="text-[11.5px] truncate">{emp.firstName} {emp.lastName}</span>
              </div>
              {cells}
              <div className="text-right">
                <div className="text-[11px] font-semibold tabular-nums">{total > 0 ? `${total.toFixed(1)}h` : '—'}</div>
                <div className="text-[9px] text-slate-400 tabular-nums">/ {emp.contractHours}h</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Rendez-vous métier empilés
// ---------------------------------------------------------------------------
function SiteAppointments({ site, weekStart }: { site: Site; weekStart: Date }) {
  const from = format(weekStart, 'yyyy-MM-dd');
  const to = format(addDays(weekStart, 6), 'yyyy-MM-dd');
  const { data: appointments } = useSiteAppointments(site.id, from, to);

  if (!appointments || appointments.length === 0) return <Empty>Aucun rendez-vous cette semaine.</Empty>;

  return (
    <div className="space-y-1">
      {appointments.map((a: any) => {
        const alert = a.status === 'ALERT';
        return (
          <div key={a.id} className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border text-[11.5px] ${alert ? 'border-red-200 bg-red-50' : 'border-slate-200'}`}>
            <span className="tabular-nums text-slate-500 w-24 flex-shrink-0">
              {format(new Date(a.startAt), 'EEE d MMM HH:mm', { locale: fr })}
            </span>
            <span className="flex-1 min-w-0 truncate">{a.catalogItem?.label ?? a.type}</span>
            {a.patientName && <span className="text-slate-500 truncate max-w-[140px]">{a.patientName}</span>}
            {alert ? (
              <span className="flex items-center gap-1 text-red-600 font-medium flex-shrink-0">
                <AlertTriangle size={11} /> personne d'habilité
              </span>
            ) : (
              <span className="text-slate-600 flex-shrink-0">
                {a.employee ? `${a.employee.firstName} ${a.employee.lastName}` : '—'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Organisation du jour empilée
// ---------------------------------------------------------------------------
function SiteOrganization({ site, date, morning }: { site: Site; date: string; morning: boolean }) {
  const { data: proposal } = useSiteOrganization(site.id, date, morning);

  if (!proposal) return <Empty>Chargement…</Empty>;
  if ((proposal as any).closed) return <Empty>Pharmacie fermée sur cette demi-journée.</Empty>;

  const alerts = (proposal as any).alerts ?? [];
  const assignments = (proposal as any).assignments ?? [];

  return (
    <div className="space-y-2">
      {alerts.length > 0 && (
        <div className="space-y-1">
          {alerts.map((al: any, i: number) => (
            <div key={i} className={`flex items-start gap-1.5 text-[11.5px] px-2.5 py-1.5 rounded-lg ${al.type === 'CRITICAL' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}>
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
              {al.message}
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1.5">
        {assignments.map((a: any) => (
          <span
            key={a.mission}
            className={`text-[11px] px-2 py-1 rounded-lg border ${
              a.status === 'COVERED' ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : a.status === 'PARTIAL' ? 'border-amber-200 bg-amber-50 text-amber-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {a.mission.replace(/_/g, ' ').toLowerCase()} · {a.presence ? (a.presentCount ?? 0) : a.employeeIds.length}/{a.targetCount}
          </span>
        ))}
        {assignments.length === 0 && <Empty>Aucun effectif requis paramétré.</Empty>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Balance des heures empilée
// ---------------------------------------------------------------------------
function SiteBalance({ site }: { site: Site }) {
  const { data } = useSiteHoursBalance(site.id, 6);
  const lines = data?.lines ?? [];
  const months = data?.months ?? [];

  if (lines.length === 0) return <Empty>Aucun salarié dans cette officine.</Empty>;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-[600px] w-full text-[11.5px]">
        <thead>
          <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="text-left font-medium pb-1.5">Salarié</th>
            {months.map((m: any) => <th key={m.key} className="text-right font-medium pb-1.5 px-2">{m.label}</th>)}
            <th className="text-right font-medium pb-1.5">Balance</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l: any) => (
            <tr key={l.employeeId} className="border-b border-slate-100">
              <td className="py-1.5 truncate max-w-[180px]">{l.name}</td>
              {months.map((m: any) => {
                const v = l.monthly?.[m.key] ?? 0;
                return (
                  <td key={m.key} className={`text-right tabular-nums px-2 ${v > 0 ? 'text-emerald-700' : v < 0 ? 'text-red-600' : 'text-slate-300'}`}>
                    {v === 0 ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}`}
                  </td>
                );
              })}
              <td className={`text-right tabular-nums font-semibold ${l.total > 0 ? 'text-emerald-700' : l.total < 0 ? 'text-red-600' : ''}`}>
                {l.total > 0 ? '+' : ''}{l.total.toFixed(1)}h
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------

export default function MultiSitePage() {
  const { data } = useSites();
  const [view, setView] = useState<ViewId>('planning');
  const [weekOffset, setWeekOffset] = useState(0);
  const [morning, setMorning] = useState(true);

  const weekStart = useMemo(
    () => addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7),
    [weekOffset]
  );
  const today = format(new Date(), 'yyyy-MM-dd');

  const sites = data?.sites ?? [];
  const needsWeek = view === 'planning' || view === 'appointments';
  // La période affichée couvre la plus large des officines : si l'une ouvre le
  // dimanche, le libellé doit aller jusqu'à lui, sinon il contredirait la grille.
  const weekSpan = sites.some((s) => s.opensOnSunday) ? 6 : 5;

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-slate-200 px-8 py-4">
        <h1 className="text-[22px] font-semibold tracking-tight">Gestion multi-site <PageHelp topic="multisite" /></h1>
        <div className="text-[12px] text-slate-500 mt-0.5">
          Toutes les officines les unes sous les autres — vue d'ensemble en lecture.
          Pour modifier, ouvrez l'officine concernée.
        </div>
      </div>

      {/* Vues + période */}
      <div className="border-b border-slate-200 px-8 py-2.5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-1">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12.5px] transition-colors ${
                  view === v.id ? 'bg-primary text-white font-medium' : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <Icon size={13} strokeWidth={1.75} />
                {v.label}
              </button>
            );
          })}
        </div>

        {needsWeek && (
          <div className="flex items-center gap-1.5">
            <button onClick={() => setWeekOffset((o) => o - 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronLeft size={15} /></button>
            <span className="text-[12px] text-slate-600 tabular-nums">
              {format(weekStart, 'd MMM', { locale: fr })} – {format(addDays(weekStart, weekSpan), 'd MMM yyyy', { locale: fr })}
            </span>
            <button onClick={() => setWeekOffset((o) => o + 1)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600"><ChevronRight size={15} /></button>
          </div>
        )}

        {view === 'organization' && (
          <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-lg p-0.5">
            <button onClick={() => setMorning(true)} className={`px-3 py-1 rounded-md text-[12px] font-medium ${morning ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Matin</button>
            <button onClick={() => setMorning(false)} className={`px-3 py-1 rounded-md text-[12px] font-medium ${!morning ? 'bg-white shadow-sm' : 'text-slate-500'}`}>Après-midi</button>
          </div>
        )}
      </div>

      {/* Les officines, empilées : on descend. */}
      <div className="flex-1 overflow-y-auto bg-slate-50">
        <div className="max-w-[1320px] mx-auto p-8 space-y-5">
          {sites.length === 0 && (
            <div className="text-[12.5px] text-slate-500">Aucune officine à afficher.</div>
          )}
          {sites.map((site) => (
            <section key={site.id} className="bg-white border border-slate-200 rounded-xl p-5">
              <SiteHeader
                site={site}
                subtitle={view === 'organization' ? (morning ? 'matin' : 'après-midi') : undefined}
              />
              {view === 'planning' && <SitePlanning site={site} weekStart={weekStart} />}
              {view === 'appointments' && <SiteAppointments site={site} weekStart={weekStart} />}
              {view === 'organization' && <SiteOrganization site={site} date={today} morning={morning} />}
              {view === 'balance' && <SiteBalance site={site} />}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
